import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import OnyxApi from "../../components/OnyxApi";
import AffordableCertifiedLandscape from "../../assets/images/Affordable-Certified-Landscape.png";
import SingleGear from "../../assets/images/single-gear.svg";
import { Modal } from "react-bootstrap";
import AiDisclosure from "../../components/AffordableOption/AiDisclosure";

function VehicleSubmission({
  dealerId,
  dealers,
  loadAiRecommendations,
  mileage,
  products,
  rates,
  setDealerId,
  setDealers,
  setLoadAiRecommendations,
  setMileage,
  setShowFullAndWrapPrograms,
  showFullAndWrapPrograms,
  setProducts,
  setRates,
  setVehicle,
  setVin,
  vin,
  handleRestart,
}) {
  const navigate = useNavigate();
  const { user, logout } = useContext(UserContext);
  const [errors, setErrors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDealers, setIsLoadingDealers] = useState(false);
  const userToken = user?.usertoken;

  const handleLogout = () => {
    handleRestart();
    logout();
    navigate("/");
  };

  const handleVinDecode = async () => {
    if (vin.length < 10) {
      return;
    }
    try {
      setErrors([]);
      const response = await OnyxApi("vinDecode", { vin }, userToken);
      setVehicle(response.data);
      return true;
    } catch (error) {
      if (error.response && error.response.status === 422) {
        const vinErrors = error.response.data.errors.vin;
        setErrors(vinErrors);
      } else {
        setErrors([error.message]);
      }
      return false;
    }
  };

  const handleGetRatings = async () => {
    try {
      setErrors([]);
      const response = await OnyxApi(
        "rates",
        { dealer_id: dealerId, mileage, vin },
        userToken,
      );
      // Filter the rates to only include Affordable Option (aka VSC) rates
      const affordableOptionRates = response.data?.filter(
        (rate) => rate.category_code === "VSC",
      );
      if (affordableOptionRates?.length < 1) {
        setErrors([
          "No Affordable Option rates found for this vehicle. The vehicle may not be eligible for the Affordable Option program.",
        ]);
        return false;
      }

      setRates(affordableOptionRates);
      return true;
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        if (status === 422) {
          const errors = error.response.data.errors;
          const flattenedErrors = Object.values(errors).flat();
          setErrors(flattenedErrors);
        } else if (status === 401 || status === 403) {
          setErrors(["Your session has expired. Please log in again."]);
        } else {
          setErrors([error.message]);
        }
      } else {
        setErrors([error.message]);
      }
      return false;
    }
  };

  const handleDealerChange = (e) => {
    const value = e.target.value;
    setDealerId(value);
  };

  const handleVinChange = (e) => {
    const value = e.target.value;
    setVin(value);
  };

  const handleMileageChange = (e) => {
    const value = e.target.value;
    setMileage(value);
  };

  const handleSubmit = async () => {
    setErrors([]);
    setIsLoading(true);
    if (!mileage || !vin) {
      setErrors(["Mileage and VIN are required"]);
      setIsLoading(false);
      return;
    }
    if (vin.length < 10) {
      setErrors(["VIN must be at least 10 characters"]);
      setIsLoading(false);
      return;
    }
    if (mileage < 1) {
      setErrors(["Mileage can't be less than 1"]);
      setIsLoading(false);
      return;
    }
    if (mileage > 150000) {
      setErrors(["Mileage can't be greater than 150,000"]);
      setIsLoading(false);
      return;
    }
    const decoded = handleVinDecode();
    if (!decoded) {
      setErrors(["Decoding VIN failed"]);
      setIsLoading(false);
      return;
    }
    const rated = await handleGetRatings();

    if (rated) {
      navigate("/recommendations");
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (!user || !user.usertoken) {
      navigate("/", { replace: true });
      return;
    }

    setErrors([]);
    const loadDealers = async () => {
      if (dealers === null && !isLoadingDealers) {
        try {
          setIsLoadingDealers(true);
          const dealersResponse = await OnyxApi(
            "dealers",
            { type: ["live", "demo"] },
            user.usertoken,
          );
          if (dealersResponse?.data) {
            const sortedDealers = dealersResponse.data.sort((a, b) =>
              a.name.localeCompare(b.name),
            );
            setDealers(sortedDealers);
            if (dealerId === "") {
              setDealerId(sortedDealers[0].id);
            }
          }
        } catch (error) {
          console.error("Error loading dealers:", error);
          setErrors([`Loading dealers failed with: ${error.message}`]);
        } finally {
          setIsLoadingDealers(false);
        }
      }
    };

    const loadProducts = async () => {
      if (products === null) {
        try {
          const data = { slug: ["component"], include: "subtype" };
          const response = await OnyxApi("products", data, userToken);
          if (response?.data) {
            setProducts(response.data);
          }
        } catch (error) {
          console.error("Error loading products:", error);
          setErrors([`Loading products failed with: ${error.message}`]);
        }
      }
    };

    loadDealers();
    loadProducts();
  }, [
    dealerId,
    user,
    dealers,
    setDealerId,
    products,
    navigate,
    setDealers,
    userToken,
    setProducts,
    isLoadingDealers,
  ]);

  return (
    <div className="vehicle-submission-page vh-100">
      <div className="ts-square" style={{ height: "300px" }}></div>
      <div className="container high-z">
        <div className="d-flex justify-content-end pt-1 pb-4">
          <button
            className="btn btn-outline-primary"
            onClick={handleLogout}
            title="Logout of the application"
          >
            Logout
          </button>
        </div>
        <div className="d-flex pt-5">
          <div className="col-5 offset-1">
            <h1 className="ps-5 title-text">
              <b className="text-900">Empowering You</b>
              <br />
              to Create an
              <br />
              <b>Affordable Option</b>
            </h1>
            <div style={{ height: "100px" }}></div>
            <h3 className="ps-5">
              <strong>It's about Affordability!</strong>
            </h3>
            <h4 className="ps-5">
              With Rising Repair Costs
              <br />
              <strong>We Care That Our Clients</strong>
              <br />
              Are Not Exposed Outside
              <br />
              The Factory Warranty
              <br />
              <strong>By 1 Day or 1 Mile</strong>
            </h4>
            <p className="mt-4 ps-5 text-start">
              <img
                src={AffordableCertifiedLandscape}
                alt="Affordable Option Certified Dealer"
                style={{ width: "70%" }}
              />
            </p>
          </div>
          <form className="col-4 col-xl-3 offset-1 mt-3 pt-5">
            {errors !== null && errors?.length > 0 ? (
              <div className="errors">
                {errors.map((error, index) => (
                  <div className="alert alert-danger" role="alert" key={index}>
                    {error}
                  </div>
                ))}
              </div>
            ) : null}
            {dealers?.length > 1 && (
              <div className="mb-3">
                <label className="visually-hidden">Dealer</label>
                <select
                  className="form-select mb-5"
                  onChange={handleDealerChange}
                  value={dealerId}
                >
                  {dealers.map((dealer) => (
                    <option key={dealer.id} value={dealer.id}>
                      {dealer.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="mb-3">
              <label className="visually-hidden">VIN</label>
              <input
                type="text"
                placeholder="*VIN"
                className="form-control mb-5"
                value={vin}
                onChange={handleVinChange}
              />
            </div>
            <div className="mb-3">
              <label className="visually-hidden">Current Mileage</label>
              <input
                type="number"
                placeholder="*Current Mileage"
                className="form-control"
                min="1"
                value={mileage}
                onChange={handleMileageChange}
              />
            </div>
            <div className="mb-3">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="loadAiRecommendationsSwitch"
                  checked={loadAiRecommendations}
                  onChange={(e) => setLoadAiRecommendations(e.target.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor="loadAiRecommendationsSwitch"
                >
                  {loadAiRecommendations
                    ? "AI Recommendations Enabled"
                    : "AI Recommendations Disabled"}
                </label>
              </div>
            </div>
            <div className="mb-3">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="showFullAndWrapProgramsSwitch"
                  checked={showFullAndWrapPrograms}
                  onChange={(e) => setShowFullAndWrapPrograms(e.target.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor="showFullAndWrapProgramsSwitch"
                >
                  {showFullAndWrapPrograms
                    ? "Full & Wrap Programs Enabled"
                    : "Full & Wrap Programs Disabled"}
                </label>
              </div>
            </div>
            <div className="text-end mb-5">
              <small className="text-primary">*Required</small>
            </div>
            <div className="text-center">
              <button
                type="button"
                className={`btn btn-lg ${
                  !vin || !mileage ? "btn-primary" : "btn-success text-white"
                }  text-900 px-3`}
                onClick={handleSubmit}
              >
                {isLoading ? (
                  <img
                    className="gear-spinner"
                    src={SingleGear}
                    alt="Loading..."
                    style={{ width: "20px", height: "20px" }}
                  />
                ) : (
                  "Go"
                )}
              </button>
            </div>
            <div className="text-center">
              <button
                type="button"
                className="btn btn-link btn-sm mt-3"
                onClick={() => setShowModal(true)}
              >
                Disclosure on the Use of Artificial Intelligence
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className="be-square" style={{ bottom: 0, right: 0 }}></div>
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            Disclosure on the Use of Artificial Intelligence
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AiDisclosure showTitle={false} />
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default VehicleSubmission;
